import { FunctionComponent, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { PageLayout } from '../../components';
import styles from './styles.module.scss';
import classNames from 'classnames';

interface FormField {
  id: string;
  type: 'text' | 'textarea' | 'email' | 'number' | 'select' | 'radio' | 'checkbox' | 'rating';
  label: string;
  placeholder?: string;
  helpText?: string;
  required: boolean;
  options?: string[];
  min?: number;
  max?: number;
}

interface FormDetail {
  title: string;
  slug: string;
  description?: string;
  submissionMode: 'anonymous' | 'with_contact' | 'optional_contact';
  fields: FormField[];
  submitButtonText: string;
  successMessage: string;
}

const SparkleStarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill={filled ? '#efa43b' : 'none'}
    stroke={filled ? '#efa43b' : 'rgba(255, 208, 150, 0.4)'}
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transition: 'all 0.2s ease',
      filter: filled ? 'drop-shadow(0 0 6px rgba(239, 164, 59, 0.6))' : 'none',
    }}
  >
    <path d="M12 2L14.8 9.2L22 12L14.8 14.8L12 22L9.2 14.8L2 12L9.2 9.2L12 2Z" />
  </svg>
);

interface RatingStarsProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (val: number) => void;
}

const RatingStars = ({ value, min = 1, max = 5, onChange }: RatingStarsProps) => {
  const [hoverVal, setHoverVal] = useState<number | null>(null);
  const total = (max || 5) - (min || 1) + 1;

  return (
    <div className={styles.ratingGroup}>
      {Array.from({ length: total }).map((_, idx) => {
        const ratingVal = (min || 1) + idx;
        const activeTarget = hoverVal !== null ? hoverVal : Number(value || 0);
        const isFilled = activeTarget >= ratingVal;
        return (
          <button
            key={idx}
            type="button"
            className={classNames(styles.starButton, { [styles.active]: isFilled })}
            onMouseEnter={() => setHoverVal(ratingVal)}
            onMouseLeave={() => setHoverVal(null)}
            onClick={() => onChange(ratingVal)}
            aria-label={`Rating ${ratingVal}`}
          >
            <SparkleStarIcon filled={isFilled} />
          </button>
        );
      })}
    </div>
  );
};

const FormPage: FunctionComponent = () => {
  const { form_slug } = useParams<{ form_slug?: string }>();
  const slug = form_slug || window.location.hash.replace('#/', '').split('/')[0] || '';

  const [form, setForm] = useState<FormDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState<Record<string, any>>({});

  useEffect(() => {
    if (!slug) {
      setErrorMsg('Niciun formular specificat.');
      setLoading(false);
      return;
    }

    const fetchForm = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        const response = await axios.get(`${API_BASE_URL}/public-forms/${slug}`);
        setForm(response.data);
      } catch (err: any) {
        console.error('Failed to load form:', err);
        setErrorMsg(err.response?.data?.error || 'Formularul nu a fost găsit sau nu este accesibil.');
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [slug]);

  const handleAnswerChange = (fieldId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleCheckboxToggle = (fieldId: string, option: string) => {
    const currentList: string[] = Array.isArray(answers[fieldId]) ? answers[fieldId] : [];
    if (currentList.includes(option)) {
      handleAnswerChange(fieldId, currentList.filter(o => o !== option));
    } else {
      handleAnswerChange(fieldId, [...currentList, option]);
    }
  };

  const handleSubmit = async () => {
    if (!form) return;
    setErrorMsg('');

    // Contact validation
    if (form.submissionMode === 'with_contact') {
      if (!email.trim() || !email.includes('@')) {
        setErrorMsg('Te rugăm să introduci o adresă de email validă.');
        return;
      }
    }

    // Required fields validation
    for (const field of form.fields) {
      if (field.required) {
        const val = answers[field.id];
        const isEmpty = val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0);
        if (isEmpty) {
          setErrorMsg(`Câmpul "${field.label}" este obligatoriu.`);
          return;
        }
      }
    }

    setSubmitting(true);

    try {
      const payloadAnswers = Object.entries(answers).map(([fieldId, value]) => ({
        fieldId,
        value,
      }));

      await axios.post(`${API_BASE_URL}/public-forms/${slug}/submit`, {
        name: name.trim(),
        email: email.trim(),
        isAnonymous: form.submissionMode === 'anonymous' || (!name.trim() && !email.trim()),
        answers: payloadAnswers,
      });

      setSubmitted(true);
    } catch (err: any) {
      console.error('Submission failed:', err);
      setErrorMsg(err.response?.data?.error || 'A apărut o eroare la trimiterea răspunsurilor.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout showTitle={false} showFooter={false}>
      <div className={styles.container}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#efa43b' }}>
            <h2>Se încarcă formularul...</h2>
          </div>
        ) : errorMsg && !form ? (
          <div className={styles.formCard} style={{ textAlign: 'center' }}>
            <h2 className={styles.title} style={{ color: '#ff6b6b' }}>Formular Inaccesibil</h2>
            <p className={styles.description}>{errorMsg}</p>
            <Link to="/" style={{ color: '#efa43b', textDecoration: 'none', border: '1px solid #efa43b', padding: '10px 20px', borderRadius: '15px' }}>
              Înapoi la pagina principală
            </Link>
          </div>
        ) : submitted ? (
          <div className={styles.formCard} style={{ textAlign: 'center' }}>
            <h2 className={styles.title}>Vă mulțumim!</h2>
            <p className={styles.description}>
              {form?.successMessage || 'Îți mulțumim pentru completarea formularului!'}
            </p>
            <div style={{ marginTop: 30 }}>
              <Link to="/" style={{ color: '#efa43b', textDecoration: 'none', border: '1px solid #efa43b', padding: '12px 24px', borderRadius: '25px', fontWeight: 'bold' }}>
                Mergi la pagina principală
              </Link>
            </div>
          </div>
        ) : (
          <div className={styles.formCard}>
            <h1 className={styles.title}>{form?.title}</h1>
            {form?.description && <p className={styles.description}>{form.description}</p>}

            {/* Contact Details Section if applicable */}
            {form?.submissionMode !== 'anonymous' && (
              <div className={styles.contactSection}>
                <h3 className={styles.contactTitle}>
                  Informații de Contact {form?.submissionMode === 'optional_contact' ? '(Opțional)' : '(Obligatoriu)'}
                </h3>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Nume și Prenume
                  </label>
                  <input
                    type="text"
                    className={styles.inputBase}
                    placeholder="Cum te numești?"
                    value={name}
                    onChange={e => setName(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    Adresă Email {form?.submissionMode === 'with_contact' && <span className={styles.required}>*</span>}
                  </label>
                  <input
                    type="email"
                    className={styles.inputBase}
                    placeholder="Adresa ta de email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Dynamic Form Fields */}
            {form?.fields.map(field => (
              <div key={field.id} className={styles.formGroup}>
                <label className={styles.label}>
                  {field.label} {field.required && <span className={styles.required}>*</span>}
                </label>
                {field.helpText && <div className={styles.helpText}>{field.helpText}</div>}

                {field.type === 'text' && (
                  <input
                    type="text"
                    className={styles.inputBase}
                    placeholder={field.placeholder || ''}
                    value={answers[field.id] || ''}
                    onChange={e => handleAnswerChange(field.id, e.target.value)}
                  />
                )}

                {field.type === 'textarea' && (
                  <textarea
                    className={styles.textareaBase}
                    placeholder={field.placeholder || ''}
                    value={answers[field.id] || ''}
                    onChange={e => handleAnswerChange(field.id, e.target.value)}
                  />
                )}

                {field.type === 'email' && (
                  <input
                    type="email"
                    className={styles.inputBase}
                    placeholder={field.placeholder || 'nume@domeniu.ro'}
                    value={answers[field.id] || ''}
                    onChange={e => handleAnswerChange(field.id, e.target.value)}
                  />
                )}

                {field.type === 'number' && (
                  <input
                    type="number"
                    className={styles.inputBase}
                    placeholder={field.placeholder || ''}
                    value={answers[field.id] ?? ''}
                    onChange={e => handleAnswerChange(field.id, e.target.value)}
                  />
                )}

                {field.type === 'select' && (
                  <select
                    className={styles.selectBase}
                    value={answers[field.id] || ''}
                    onChange={e => handleAnswerChange(field.id, e.target.value)}
                  >
                    <option value="">Alege o opțiune...</option>
                    {field.options?.map((opt, idx) => (
                      <option key={idx} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}

                {field.type === 'radio' && (
                  <div className={styles.optionsGroup}>
                    {field.options?.map((opt, idx) => {
                      const isChecked = answers[field.id] === opt;
                      return (
                        <label
                          key={idx}
                          className={classNames(styles.optionLabel, { [styles.selected]: isChecked })}
                        >
                          <input
                            type="radio"
                            name={field.id}
                            checked={isChecked}
                            onChange={() => handleAnswerChange(field.id, opt)}
                          />
                          <span className={styles.controlIndicator} />
                          <span className={styles.optionText}>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {field.type === 'checkbox' && (
                  <div className={styles.optionsGroup}>
                    {field.options?.map((opt, idx) => {
                      const currentAnswers: string[] = Array.isArray(answers[field.id]) ? answers[field.id] : [];
                      const isChecked = currentAnswers.includes(opt);
                      return (
                        <label
                          key={idx}
                          className={classNames(styles.optionLabel, { [styles.selected]: isChecked })}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleCheckboxToggle(field.id, opt)}
                          />
                          <span className={styles.controlIndicator} />
                          <span className={styles.optionText}>{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {field.type === 'rating' && (
                  <RatingStars
                    value={answers[field.id]}
                    min={field.min}
                    max={field.max}
                    onChange={val => handleAnswerChange(field.id, val)}
                  />
                )}
              </div>
            ))}

            {errorMsg && (
              <div className={classNames(styles.message, styles.error)}>
                {errorMsg}
              </div>
            )}

            <button
              className={styles.actionButton}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Se trimite...' : (form?.submitButtonText || 'Trimite Răspunsul')}
            </button>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default FormPage;
